import { ChevronRight } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import { cn } from '~/utils/cn';

import type { BatchCarryover } from '../../types/batch-retry.api.types';

import {
  balanceLine,
  batchLabel,
  formatCount,
  humanize,
  splitBatchName,
} from './batch-retry-utils';

/** Which side of the carry-over a "View ›" opens in the products grid. */
export interface CarryoverView {
  side: 'EARLIER' | 'MOVED';
  batchId: string;
}

interface Row {
  jobId: string;
  name: string;
  createdAt: string | null;
  status: string | null;
  count: number;
  enriched: number;
  notEnriched: number;
}

interface SectionProps {
  testId: string;
  title: string;
  explainer: string;
  countLabel: string;
  rows: Row[];
  total: Row;
  onView: (batchId: string) => void;
}

/** Green enriched / amber not enriched, against what the table covers (not red: nothing failed here). */
function SplitBar({
  enriched,
  notEnriched,
  testId,
}: {
  enriched: number;
  notEnriched: number;
  testId: string;
}) {
  const total = enriched + notEnriched;
  const pct = (n: number) => `${String(total > 0 ? (n / total) * 100 : 0)}%`;
  return (
    <div
      role="img"
      aria-label={`${formatCount(enriched)} enriched, ${formatCount(notEnriched)} not enriched`}
      data-testid={testId}
      className="bg-muted flex h-2 gap-0.5 overflow-hidden rounded-full"
    >
      {enriched > 0 && <span className="h-full bg-emerald-500" style={{ width: pct(enriched) }} />}
      {notEnriched > 0 && (
        <span className="bg-warning h-full" style={{ width: pct(notEnriched) }} />
      )}
    </div>
  );
}

/** Batches shown before a carry-over table scrolls (one subsidiary can carry from dozens). */
const MAX_VISIBLE_ROWS = 5;

function CarryoverSection({
  testId,
  title,
  explainer,
  countLabel,
  rows,
  total,
  onView,
}: SectionProps) {
  return (
    <div data-testid={testId} className="space-y-3 rounded-lg border p-4">
      <header className="space-y-0.5">
        <h3 className="text-sm font-semibold">
          {title} · <span className="tabular-nums">{formatCount(total.count)}</span>
        </h3>
        <p className="text-muted-foreground text-xs">
          {explainer}
          {rows.length > MAX_VISIBLE_ROWS && (
            // Overlay scrollbars hide until scrolled: say there is more than what shows.
            <span data-testid={`${testId}-scroll-hint`}>
              {' '}
              {formatCount(rows.length)} batches, scroll for all.
            </span>
          )}
        </p>
      </header>
      <SplitBar
        enriched={total.enriched}
        notEnriched={total.notEnriched}
        testId={`${testId}-bar`}
      />
      {/* Same table as the products grid. Past {@link MAX_VISIBLE_ROWS} batches the rows scroll
          inside it; header and Total stay pinned. */}
      <Table
        data-testid={`${testId}-table`}
        containerClassName={cn(
          'rounded-md border',
          rows.length > MAX_VISIBLE_ROWS && 'max-h-72 overflow-y-auto',
        )}
      >
        <TableHeader className="bg-background sticky top-0 z-10 shadow-[0_1px_0_var(--border)]">
          <TableRow className="hover:bg-transparent">
            <TableHead className="h-10">Batch</TableHead>
            <TableHead className="h-10 text-right">{countLabel}</TableHead>
            <TableHead className="h-10 text-right">Enriched</TableHead>
            <TableHead className="h-10 text-right">Not enriched</TableHead>
            <TableHead className="h-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const { title: name, date } = splitBatchName(batchLabel(row));
            return (
              <TableRow key={row.jobId} data-testid={`${testId}-row-${row.jobId}`}>
                <TableCell className="py-3">
                  <span className="font-semibold">{name}</span>
                  {date && <span className="text-muted-foreground"> · {date}</span>}
                  {row.status && (
                    <span className="bg-muted text-muted-foreground ml-2 rounded-full px-2 py-0.5 text-xs">
                      {humanize(row.status)}
                    </span>
                  )}
                </TableCell>
                <TableCell className="py-3 text-right tabular-nums">
                  {formatCount(row.count)}
                </TableCell>
                <TableCell className="py-3 text-right tabular-nums">
                  {formatCount(row.enriched)}
                </TableCell>
                <TableCell
                  className={cn(
                    'py-3 text-right tabular-nums',
                    row.notEnriched > 0 && 'text-warning',
                  )}
                >
                  {row.notEnriched > 0 ? formatCount(row.notEnriched) : '—'}
                </TableCell>
                <TableCell className="py-3 text-right">
                  <button
                    type="button"
                    data-testid={`${testId}-view-${row.jobId}`}
                    className="text-primary inline-flex cursor-pointer items-center text-xs font-medium hover:underline"
                    onClick={() => {
                      onView(row.jobId);
                    }}
                  >
                    View <ChevronRight className="size-3" aria-hidden="true" />
                  </button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
        <TableFooter className="bg-muted sticky bottom-0 z-10">
          <TableRow data-testid={`${testId}-total`} className="font-semibold hover:bg-transparent">
            <TableCell className="py-3">Total</TableCell>
            <TableCell className="py-3 text-right tabular-nums">
              {formatCount(total.count)}
            </TableCell>
            <TableCell className="py-3 text-right tabular-nums">
              {formatCount(total.enriched)}
            </TableCell>
            <TableCell className="py-3 text-right tabular-nums">
              {total.notEnriched > 0 ? formatCount(total.notEnriched) : '—'}
            </TableCell>
            <TableCell className="py-3" />
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  );
}

export interface BatchCarryoverSectionProps {
  carryover: BatchCarryover;
  onView: (view: CarryoverView) => void;
}

/**
 * Runs panel, under the tiles: which earlier batches this batch picked products up from, and/or
 * which later batches picked up products this one left. Renders nothing when there is neither.
 */
export function BatchCarryoverSection({ carryover, onView }: BatchCarryoverSectionProps) {
  const carriedIn = carryover.carriedIn;
  const movedOut = carryover.movedOut;
  const hasIn = !!carriedIn && carriedIn.total > 0;
  const hasOut = !!movedOut && movedOut.total > 0;
  if (!hasIn && !hasOut) return null;
  return (
    <div data-testid="batch-carryover" className="space-y-3">
      {/* Both directions: side by side on wide screens (each card stretches to the taller one). */}
      <div
        data-testid="batch-carryover-tables"
        className={cn('grid gap-3', hasIn && hasOut && 'lg:grid-cols-2')}
      >
        {hasIn && (
          <CarryoverSection
            testId="batch-carryover-from"
            title="From earlier batches"
            explainer="Products earlier batches curated but never enriched, enriched in this batch."
            countLabel="Carried"
            rows={carriedIn.bySource.map((s) => ({
              jobId: s.jobId,
              name: s.name,
              createdAt: s.createdAt,
              status: s.status,
              count: s.carried,
              enriched: s.enriched,
              notEnriched: s.carried - s.enriched,
            }))}
            total={{
              jobId: 'total',
              name: 'Total',
              createdAt: null,
              status: null,
              count: carriedIn.total,
              enriched: carriedIn.enriched,
              notEnriched: carriedIn.total - carriedIn.enriched,
            }}
            onView={(batchId) => {
              onView({ side: 'EARLIER', batchId });
            }}
          />
        )}
        {hasOut && (
          <CarryoverSection
            testId="batch-carryover-moved"
            title="Moved to later batches"
            explainer="These products were picked up by later batches. This batch's own record is unchanged."
            countLabel="Moved"
            rows={movedOut.byBatch.map((b) => ({
              jobId: b.jobId,
              name: b.name,
              createdAt: b.createdAt,
              status: b.status,
              count: b.moved,
              enriched: b.enriched,
              notEnriched: b.notEnriched,
            }))}
            total={{
              jobId: 'total',
              name: 'Total',
              createdAt: null,
              status: null,
              count: movedOut.total,
              enriched: movedOut.enriched,
              notEnriched: movedOut.notEnriched,
            }}
            onView={(batchId) => {
              onView({ side: 'MOVED', batchId });
            }}
          />
        )}
      </div>
      <p
        data-testid="batch-carryover-balance"
        className="text-muted-foreground text-xs tabular-nums"
      >
        {balanceLine(carryover.balance)}
      </p>
    </div>
  );
}
